import { SkyButton, SkyText } from "@skypointcloud/skypoint-ui";
import { useMemo, useState, type ReactNode } from "react";
import { Route, Switch } from "wouter";
import { ErrorState, LoadingRows, PermissionNotice } from "@/components/access/ScreenStates";
import { TopbarActionsPortal } from "@/components/navigation/TopbarActionsPortal";
import {
  InstanceSettingsForm,
  settingsDraftErrors,
  draftFromSettings,
  type SettingsDraft,
} from "@/components/settings/InstanceSettingsForm";
import { ReplaceSecretDialog } from "@/components/settings/ReplaceSecretDialog";
import { SettingsTabs } from "@/components/settings/SettingsTabs";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { useDismissNoticeWhen, useNotice } from "@/lib/feedback/NoticeProvider";
import { useApiErrorNotice } from "@/lib/feedback/useApiErrorNotice";
import { usePermissions } from "@/hooks/use-permissions";
import { useInstance, useInstanceSettings, useSaveInstanceSettings } from "@/hooks/use-settings";
import ConnectorAccessTab from "@/pages/admin/connector-access";
import AdminNotificationsPage from "@/pages/admin/notifications";
import type { InstanceSettingsBody, InstanceSettingsView } from "@shared/types/settings";

/**
 * Instance settings (canvas 08 block 03; FR-57, FR-72).
 *
 * The route guard already refuses a reader without `instance.settings.write`, so
 * this screen is a write surface throughout; the read-only branch below exists
 * for the case where the permission is lost between the route check and the
 * render, and says which permission is missing rather than showing dead controls.
 *
 * The save sends only what changed. `instanceSettingsBodySchema` refuses an empty
 * body, so an unchanged form does not get a Save it can only fail with.
 */
function buildBody(
  draft: SettingsDraft,
  settings: InstanceSettingsView,
): InstanceSettingsBody | null {
  const body: Record<string, unknown> = {};
  const retention = Number(draft.retentionDays);
  const concurrency = Number(draft.maxConcurrentRuns);
  if (Number.isFinite(retention) && retention !== settings.retentionDays) {
    body.retentionDays = retention;
  }
  if (Number.isFinite(concurrency) && concurrency !== settings.maxConcurrentRuns) {
    body.maxConcurrentRuns = concurrency;
  }
  if (draft.notifyOnSuccess !== settings.notifyOnSuccess) {
    body.notifyOnSuccess = draft.notifyOnSuccess;
  }
  if (draft.teamsWebhookUrl !== undefined) body.teamsWebhookUrl = draft.teamsWebhookUrl;
  return Object.keys(body).length > 0 ? (body as InstanceSettingsBody) : null;
}

/** "Settings saved", cleared once the form differs from what was saved (ux-s10 §3). */
const SAVED_NOTICE_KEY = "instance-settings-saved";

/**
 * The Settings section: the General tab at its root and, nested under it, the Notifications tab
 * (contract-notif R10) and the Connectors tab (T-51, the tenant's connector choice). `App.tsx` mounts this route with `nest`, so `/notifications` here is
 * `/admin/settings/notifications`; the tab inherits this route's guard and adds no nav entry.
 */
export default function AdminSettingsPage() {
  return (
    <Switch>
      <Route path="/notifications">
        <AdminNotificationsPage />
      </Route>
      <Route path="/connectors">
        <ConnectorAccessTab />
      </Route>
      <Route>
        <InstanceSettingsTab />
      </Route>
    </Switch>
  );
}

/** The loaded form, the writer's save bar, and the webhook's replace dialog. */
function LoadedSettings({
  instance,
  settings,
  draft,
  canWrite,
  canSave,
  canDiscard,
  saving,
  replacingWebhook,
  onChange,
  onSave,
  onReplacingWebhookChange,
}: {
  instance: ReturnType<typeof useInstance>["data"];
  settings: InstanceSettingsView;
  draft: SettingsDraft;
  canWrite: boolean;
  canSave: boolean;
  /** Unsaved changes, valid or not, and no save in flight (#1100). */
  canDiscard: boolean;
  saving: boolean;
  replacingWebhook: boolean;
  onChange: (draft: SettingsDraft) => void;
  onSave: () => void;
  onReplacingWebhookChange: (open: boolean) => void;
}) {
  return (
    <>
      <InstanceSettingsForm
        instance={instance}
        settings={settings}
        draft={draft}
        readOnly={!canWrite}
        onChange={onChange}
        onReplaceWebhook={() => onReplacingWebhookChange(true)}
      />

      {canWrite ? (
        <>
          <TopbarActionsPortal>
            <SkyButton
              appearance="primary"
              testId="save-settings-topbar"
              disabled={!canSave}
              loading={saving}
              onClick={onSave}
            >
              Save changes
            </SkyButton>
          </TopbarActionsPortal>

          <div className="flex flex-wrap items-center gap-3 border-t border-border pt-3">
            <SkyText as="p" size={200} color="muted">
              This change affects the next run only, except the import limit, which applies at once,
              also to runs and loads that are waiting. A run already in flight keeps its other
              settings.
            </SkyText>
            <span className="ml-auto flex items-center gap-2">
              <SkyButton
                appearance="subtle"
                testId="discard-settings"
                disabled={!canDiscard}
                onClick={() => onChange(draftFromSettings(settings))}
              >
                Discard
              </SkyButton>
              <SkyButton
                appearance="primary"
                testId="save-settings"
                disabled={!canSave}
                loading={saving}
                onClick={onSave}
              >
                Save changes
              </SkyButton>
            </span>
          </div>
        </>
      ) : null}

      <ReplaceSecretDialog
        open={replacingWebhook}
        title="Replace the Teams webhook"
        label="Webhook URL"
        multiline={false}
        hint="An absolute https URL. It is written to the vault and never read back."
        onOpenChange={onReplacingWebhookChange}
        onStage={(value) => onChange({ ...draft, teamsWebhookUrl: value })}
      />
    </>
  );
}

function InstanceSettingsTab() {
  const { can, isResolved } = usePermissions();
  const { selectedInstance } = useTenantInstance();
  const notice = useNotice();
  const showError = useApiErrorNotice();
  const canWrite = can("instance.settings.write");

  // Nothing is asked for until an instance is SELECTED: the `x-instance-id`
  // header that scopes these routes is derived from that choice, so a query
  // fired before `/api/v2/me` has resolved would go out with no header at all.
  const ready = isResolved && Boolean(selectedInstance);
  const instance = useInstance(ready);
  const { data: settings, isPending, isError, error, refetch } = useInstanceSettings(ready);
  const save = useSaveInstanceSettings();

  const [draft, setDraft] = useState<SettingsDraft | null>(null);
  const [isReplacingWebhook, setReplacingWebhook] = useState(false);

  // Seeded during render from the first settings document the query yields; see
  // the note in `pages/destination.tsx` for why not in an effect.
  if (settings && draft === null) setDraft(draftFromSettings(settings));

  const body = useMemo(
    () => (draft && settings ? buildBody(draft, settings) : null),
    [draft, settings],
  );
  // Null right after a save: the mutation writes the returned view into the cache first.
  useDismissNoticeWhen(SAVED_NOTICE_KEY, body !== null);

  if (!isResolved) return <LoadingRows rows={4} columns={2} />;
  if (!canWrite && !can("instance.read")) {
    return <PermissionNotice permission="instance.settings.write" />;
  }

  function handleSave() {
    if (!body) return;
    save.mutate(body, {
      onSuccess: (view) => {
        setDraft(draftFromSettings(view));
        notice.success("Settings saved. The change applies to the next import.", {
          key: SAVED_NOTICE_KEY,
        });
      },
      onError: (cause) => showError(cause, "The settings could not be saved."),
    });
  }

  function renderSettings(): ReactNode {
    if (isPending) {
      return <LoadingRows rows={4} columns={2} />;
    }
    if (isError) {
      return (
        <ErrorState
          title="The settings did not load"
          error={error}
          onRetry={() => void refetch()}
        />
      );
    }
    if (settings && draft) {
      return (
        <LoadedSettings
          instance={instance.data}
          settings={settings}
          draft={draft}
          canWrite={canWrite}
          canSave={body !== null && Object.keys(settingsDraftErrors(draft)).length === 0}
          canDiscard={body !== null && !save.isPending}
          saving={save.isPending}
          replacingWebhook={isReplacingWebhook}
          onChange={setDraft}
          onSave={handleSave}
          onReplacingWebhookChange={setReplacingWebhook}
        />
      );
    }
    return null;
  }

  return (
    <div className="space-y-4">
      <SettingsTabs active="settings" />

      {!canWrite ? <PermissionNotice permission="instance.settings.write" /> : null}

      {renderSettings()}
    </div>
  );
}
